import { IS_BETWEEN, IS_GREATER, IS_LESS } from '../constants/filter';

const SUFFIX_MULTIPLIERS = {
  k: 1_000,
  m: 1_000_000,
};

const AMOUNT_SUFFIX_RE = /^(\d*\.?\d*)([km])$/i;
const NON_NUMERIC_RE = /[^\d.]/g;
const EXTRA_DOT_RE = /\./g;
const THOUSANDS_SEP_RE = /\B(?=(\d{3})+(?!\d))/g;

/**
 * Expand a numeric base by a trailing k/K (thousands) or m/M (millions) suffix.
 * @param {string} base
 * @param {string} suffix
 * @returns {string}
 */
function applyAmountSuffix(base, suffix) {
  const multiplier = SUFFIX_MULTIPLIERS[String(suffix).toLowerCase()];
  if (!multiplier || !base || base === '.') return '';

  const product = Number(base) * multiplier;
  if (!Number.isFinite(product)) return '';

  // Prefer whole dollars for suffix shorthand; keep up to 2 decimals otherwise.
  const rounded = Math.abs(product - Math.round(product)) < 1e-9 ? Math.round(product) : Number(product.toFixed(2));
  return String(rounded);
}

/**
 * Keep a clean numeric string: digits and at most one decimal point.
 * Preserves a trailing "." while the user is typing a fractional value.
 * @param {string} value
 * @returns {string}
 */
function cleanNumericString(value) {
  const cleaned = String(value).replaceAll(NON_NUMERIC_RE, '');
  if (!cleaned) return '';

  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;

  const intPart = cleaned.slice(0, firstDot);
  const decPart = cleaned.slice(firstDot + 1).replaceAll(EXTRA_DOT_RE, '');
  if (cleaned.endsWith('.') && decPart === '') {
    return `${intPart}.`;
  }
  return decPart ? `${intPart}.${decPart}` : intPart;
}

/**
 * Parse typed amount into a plain numeric string for storage (no commas / $ / suffixes).
 * Accepts existing commas, expands trailing k/K (×1,000) and m/M (×1,000,000).
 * @param {*} value
 * @returns {string}
 */
export function sanitizeAmountInput(value) {
  if (value == null) return '';

  const raw = String(value).trim().replaceAll('$', '').replaceAll(',', '').replaceAll(/\s/g, '');
  if (!raw) return '';

  const suffixMatch = AMOUNT_SUFFIX_RE.exec(raw);
  if (suffixMatch) {
    const [, base, suffix] = suffixMatch;
    return applyAmountSuffix(base, suffix);
  }

  return cleanNumericString(raw);
}

/**
 * Build the display string for the integer/decimal parts of an amount.
 * @param {string} intPart
 * @param {string|undefined} decPart
 * @param {boolean} trailingDot
 * @returns {string}
 */
function buildCommaFormattedAmount(intPart, decPart, trailingDot) {
  const formattedInt = (intPart || '0').replaceAll(THOUSANDS_SEP_RE, ',');

  if (!intPart && trailingDot) {
    return '0.';
  }
  if (!intPart && decPart !== undefined) {
    return `0.${decPart}`;
  }
  if (decPart !== undefined) {
    return `${formattedInt}.${decPart}`;
  }
  if (trailingDot) {
    return `${formattedInt}.`;
  }
  return formattedInt;
}

/**
 * Format a stored numeric string with thousands separators for readability.
 * @param {*} value
 * @returns {string}
 */
export function formatAmountWithCommas(value) {
  if (value == null || value === '') return '';

  const str = String(value);
  const negative = str.startsWith('-');
  const unsigned = negative ? str.slice(1) : str;
  const trailingDot = unsigned.endsWith('.');
  const [intPart = '', ...rest] = unsigned.split('.');
  let decPart;
  if (rest.length) {
    decPart = rest.join('');
  } else if (trailingDot) {
    decPart = '';
  }

  if (intPart && !/^\d+$/.test(intPart)) {
    const sanitized = sanitizeAmountInput(str);
    return sanitized ? formatAmountWithCommas(sanitized) : '';
  }

  const result = buildCommaFormattedAmount(intPart, decPart, trailingDot);
  return negative ? `-${result}` : result;
}

/**
 * Coerce a stored/display amount to a finite number (strips commas, expands k/M).
 * @param {*} value
 * @returns {number|undefined}
 */
export function amountToNumber(value) {
  if (value === undefined || value === '' || value == null) return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;

  const sanitized = sanitizeAmountInput(value);
  if (!sanitized || sanitized === '.') return undefined;

  const number = Number(sanitized);
  return Number.isFinite(number) ? number : undefined;
}

/**
 * Whether a bound field is considered set.
 * @param {*} value
 * @returns {boolean}
 */
export function hasAmountBound(value) {
  return value != null && value !== '';
}

/**
 * Normalize min/max form fields into the shared number-filter shape.
 * @param {*} from
 * @param {*} to
 * @returns {{ type: string, data: string|number, from: string|number, to: string|number }}
 */
export function normalizeAmountRangeValues(from = '', to = '') {
  const hasFrom = hasAmountBound(from);
  const hasTo = hasAmountBound(to);

  if (hasFrom && hasTo) {
    return { type: IS_BETWEEN, data: '', from, to };
  }
  if (hasFrom) {
    return { type: IS_GREATER, data: from, from, to: '' };
  }
  if (hasTo) {
    return { type: IS_LESS, data: to, from: '', to };
  }
  return { type: IS_GREATER, data: '', from: '', to: '' };
}

/**
 * Resolve one bound from explicit value or fallback data for a comparison type.
 * @param {*} bound
 * @param {*} data
 * @param {string} type
 * @param {string} expectedType
 * @returns {string|number}
 */
function resolveDisplayBound(bound, data, type, expectedType) {
  if (hasAmountBound(bound)) return bound;
  if (type === expectedType && hasAmountBound(data)) return data;
  return '';
}

/**
 * Resolve display min/max from a stored filter state (supports reopen after Apply).
 * @param {{ type?: string, data?: *, from?: *, to?: * }} values
 * @returns {{ from: string|number, to: string|number }}
 */
export function getAmountRangeDisplayBounds(values = {}) {
  const { type, data, from = '', to = '' } = values;

  if (hasAmountBound(from) || hasAmountBound(to)) {
    return {
      from: resolveDisplayBound(from, data, type, IS_GREATER),
      to: resolveDisplayBound(to, data, type, IS_LESS),
    };
  }

  if (type === IS_GREATER && hasAmountBound(data)) {
    return { from: data, to: '' };
  }
  if (type === IS_LESS && hasAmountBound(data)) {
    return { from: '', to: data };
  }

  return { from: '', to: '' };
}

/**
 * Format a numeric chip value as USD when the filter uses a $ adornment.
 * @param {*} value
 * @param {{ options?: { leftAdorment?: string, formatAsCurrency?: boolean } }} filterOption
 * @returns {*}
 */
export function formatAmountFilterChipValue(value, filterOption = {}) {
  const options = filterOption.options || {};
  const useCurrency = options.formatAsCurrency || options.leftAdorment === '$';
  if (!useCurrency || !hasAmountBound(value)) return value;

  const number = amountToNumber(value);
  if (number === undefined) return value;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number.isInteger(number) ? 0 : 2,
    minimumFractionDigits: 0,
  }).format(number);
}
